/** Owned inspection only. The parent retains repair admission and mutation
 * ownership; lookup failure never establishes that repair is safe. */
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Wrench } from 'lucide-react';
import { getOrderReservationHold } from '@/api/runs';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/config/constants';
import { getActiveSessionRevision, subscribeActiveSessionRevision, getAuthTokenForRequest } from '@/api/authenticatedClient';

// Validate the descriptive contract, not repair eligibility. Older or
// contradictory responses cannot be presented as established facts.
const validHold = (hold, orderId) => {
  if (!hold || hold.order_id !== orderId || typeof hold.run_exists !== 'boolean'
    || typeof hold.can_clear !== 'boolean'
    || !(hold.run_name == null || typeof hold.run_name === 'string')) return false;
  if (hold.hold_state === 'no_reservation') return hold.draft_run_id === null && !hold.can_clear && !hold.run_exists;
  if (typeof hold.draft_run_id !== 'string' || !hold.draft_run_id) return false;
  if (hold.hold_state === 'clearable') return hold.can_clear;
  return ['held', 'not_repairable'].includes(hold.hold_state) && !hold.can_clear;
};

const RepairReservationDialog = ({ pending, onConfirm, onCancel, commandPending = false }) => {
  const { sessionBinding } = useAuth();
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision, getActiveSessionRevision);
  const scope = useMemo(() => ({ pending, orderId: pending?.orderId, binding: sessionBinding, revision, api: API }),
    [pending, sessionBinding, revision]);
  const liveScope = useRef(scope);
  liveScope.current = scope;
  const attempt = useRef(null);
  const closedScope = useRef(null);
  const mutationScope = useRef(null);
  const [result, setResult] = useState(null);
  const [repairingScope, setRepairingScope] = useState(null);
  const retire = useCallback(() => {
    if (attempt.current) {
      attempt.current.active = false;
      attempt.current.controller.abort();
    }
  }, []);
  const admitted = useCallback(() => liveScope.current === scope && closedScope.current !== scope
    && scope.pending && scope.binding?.revision === scope.revision
    && scope.revision === getActiveSessionRevision()
    && scope.binding?.source === 'cloud' && scope.binding.user?.roles?.includes('admin')
    && scope.api !== null && Boolean(getAuthTokenForRequest(`${scope.api}/runs/reservations/${scope.orderId}`)), [scope]);
  const read = useCallback(async () => {
    if (!scope.pending || liveScope.current !== scope || closedScope.current === scope || mutationScope.current === scope) return;
    retire();
    const controller = new AbortController();
    const url = `${scope.api}/runs/reservations/${scope.orderId}`;
    const token = getAuthTokenForRequest(url);
    const current = { active: true, controller };
    attempt.current = current;
    const publish = (status, hold = null) => {
      if (current.active && attempt.current === current && liveScope.current === scope
        && scope.revision === getActiveSessionRevision() && token === getAuthTokenForRequest(url)) {
        setResult({ scope, status, hold });
      }
    };
    if (!admitted()) { publish('permission'); return; }
    publish('loading');
    try {
      const hold = await getOrderReservationHold(scope.orderId, {
        signal: controller.signal, headers: { Authorization: `Bearer ${token}` },
      });
      publish(validHold(hold, scope.orderId) ? hold.hold_state : 'unavailable', hold);
    } catch (error) {
      const status = error?.response?.status;
      publish(status === 404 ? 'missing' : [401, 403].includes(status) ? 'permission' : 'unavailable');
    }
  }, [scope, retire, admitted]);
  useLayoutEffect(() => {
    closedScope.current = null;
    void read();
    return retire;
  }, [read, retire]);

  const view = result?.scope === scope && revision === getActiveSessionRevision()
    ? result : { status: 'loading', hold: null };
  const repairing = repairingScope === scope;
  const canClear = admitted() && ['clearable', 'unavailable'].includes(view.status);
  const close = () => {
    closedScope.current = scope;
    retire();
    setResult({ scope, status: 'closed', hold: null });
    onCancel();
  };
  const handleConfirm = async () => {
    if (commandPending || !canClear || !admitted() || mutationScope.current === scope) return;
    mutationScope.current = scope;
    retire();
    setRepairingScope(scope);
    try {
      await onConfirm();
    } finally {
      // A dispatched repair belongs to the parent even after this opening ends.
      if (liveScope.current === scope) setRepairingScope(null);
    }
  };
  if (!pending || view.status === 'closed') return null;

  let body;
  switch (view.status) {
    case 'loading': body = <p>Checking the order's reservation…</p>; break;
    case 'no_reservation': body = <p>No reservation marker is currently recorded for this order. This does not establish whether a run lists the order.</p>; break;
    case 'held': body = <><p>Held by <strong>{view.hold.run_name || 'another run'}</strong>.</p><p>Remove the order from that run, then add it here.</p></>; break;
    case 'not_repairable': body = <p>This reservation cannot currently be repaired because the order is not in a repairable status.</p>; break;
    case 'clearable': body = <><p>{view.hold.run_exists
      ? <><strong>{view.hold.run_name || 'The referenced run'}</strong> still exists but no longer lists this order.</>
      : 'The referenced run no longer exists.'}</p><p>The reservation marker can be cleared. The server will recheck before making any change.</p></>; break;
    case 'missing': body = <p>The order could not be found. Close this dialog and refresh the order list.</p>; break;
    case 'permission': body = <p>Reservation inspection and repair require a current online admin session. Sign in or check your permissions.</p>; break;
    default: body = <><p>The reservation could not be checked. No conclusion about the run or hold is available.</p><p>You may retry the lookup or ask the server to attempt repair. It will recheck the order and run membership and may refuse.</p></>;
  }
  return (
    <Dialog open onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent data-testid="reservation-hold-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wrench className="h-5 w-5 text-amber-600" />Order reservation</DialogTitle>
          <DialogDescription asChild><div className="space-y-2">{body}</div></DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={close} data-testid="reservation-hold-close">Close</Button>
          {view.status === 'unavailable' && admitted() && <Button variant="outline" onClick={read} disabled={repairing} data-testid="reservation-hold-retry">Retry lookup</Button>}
          {canClear && <Button onClick={handleConfirm} disabled={commandPending || repairing || mutationScope.current === scope} data-testid="reservation-hold-confirm">
            {repairing ? 'Clearing…' : view.status === 'unavailable' ? 'Attempt repair' : 'Clear the hold'}
          </Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
export default RepairReservationDialog;
